'use client';
import React from 'react';
import { isSplitNode, isTabsNode, MosaicNode } from 'react-mosaic-component';

const compareWorkspaceConfigs = (
  prev: MosaicNode<string> | null | undefined,
  next: MosaicNode<string> | null | undefined,
): boolean => {
  if (prev == null || next == null) {
    return prev === next;
  }

  if (typeof prev !== 'object' || typeof next !== 'object') {
    return prev === next;
  }

  if (prev.type !== next.type) {
    return false;
  }

  if (isSplitNode(prev) && isSplitNode(next)) {
    return (
      prev.direction === next.direction &&
      prev.children.length === next.children.length &&
      prev.children.every((child, index) => compareWorkspaceConfigs(child, next.children[index]))
    );
  }

  if (isTabsNode(prev) && isTabsNode(next)) {
    return prev.tabs.length === next.tabs.length && prev.tabs.every((tab, index) => tab === next.tabs[index]);
  }

  return false;
};
export interface WorkspaceContextProps {
  config: MosaicNode<string> | null;
  configWidgetsLocationsChanged: boolean;
  isActiveWorkspace: boolean;
  isWidgetPositionChanging: boolean;
}

export const WorkspaceContext = React.createContext<WorkspaceContextProps>({
  config: null,
  configWidgetsLocationsChanged: false,
  isActiveWorkspace: true,
  isWidgetPositionChanging: false,
});

export const WorkspaceContextProvider: React.FC<
  React.PropsWithChildren<Partial<Omit<WorkspaceContextProps, 'configWidgetsLocationsChanged'>>>
> = props => {
  const [prevWorkspaceConfig, setPrevWorkspaceConfig] = React.useState<MosaicNode<string> | null | undefined>(
    undefined,
  );

  const value = React.useMemo(
    () => ({
      config: props.config ?? null,
      configWidgetsLocationsChanged: !compareWorkspaceConfigs(props.config, prevWorkspaceConfig),
      isActiveWorkspace: props.isActiveWorkspace ?? false,
      isWidgetPositionChanging: props.isWidgetPositionChanging ?? false,
    }),
    [prevWorkspaceConfig, props.config, props.isActiveWorkspace, props.isWidgetPositionChanging],
  );

  React.useEffect(() => {
    setPrevWorkspaceConfig(props.config);
  }, [props.config]);

  return <WorkspaceContext.Provider value={value}>{props.children}</WorkspaceContext.Provider>;
};
